import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import {
    selectCompany,
    selectUac,
    selectVerificationDetails,
} from 'store/common/selectors/profile';
import { selectSessionToken } from 'features/onboarding/state/selectors';
import { resolveRoute, SCOPES } from '../steps';
import { isOnFlow, pauseActive, resumeActive, setOnFlow } from '../journeyContext';
import { trackIssue, trackJourneyEnded, trackPageLeft, trackStep } from '../track';

/**
 * Follows the Regula/AWS journey across every screen it can show, including the error, timeout and
 * attempts screens it shares with the other flows, which sit outside its own route tree.
 */
export const useGovtIdV2Journey = () => {
    const { pathname } = useLocation();

    const linkUuid = useSelector(selectSessionToken);
    const uac = useSelector(selectUac);
    const company = useSelector(selectCompany);
    const verificationDetails = useSelector(selectVerificationDetails);

    useEffect(() => {
        if (!linkUuid) return;

        const { step, scope, errorScreen, terminal, finalStatus, issue } = resolveRoute(pathname);

        if (scope === SCOPES.V2) setOnFlow(true);
        if (!isOnFlow()) return;

        if (!scope) {
            setOnFlow(false);
            return;
        }

        // Every load resets the store and refetches the profile; a step seen before the user is
        // known would be recorded but never sent.
        if (!uac) return;

        if (document.visibilityState === 'visible') resumeActive();

        const isNewStep = trackStep(step, { errorScreen });

        if (isNewStep && issue) trackIssue(issue);

        if (!terminal) return;

        trackJourneyEnded({
            finalStatus,
            company,
            document_attempts_used: verificationDetails?.govt_id_attempts,
            selfie_attempts_used: verificationDetails?.selfie_attempts,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname, linkUuid, uac]);

    useEffect(() => {
        let hasLeft = false;

        const leave = () => {
            pauseActive();

            if (hasLeft) return;

            hasLeft = true;
            trackPageLeft();
        };

        // Mobile browsers often skip `pagehide` when a tab is swiped away; a hidden page is the last
        // moment they reliably let us send.
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') return leave();

            hasLeft = false;
            return resumeActive();
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('pagehide', leave);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('pagehide', leave);
        };
    }, []);
};
